// @ts-check
/**
 * SeedResolver - single source of truth for the seed that drives both the
 * procedural maze layout (MazeGenerator) and the video shuffle (ConfigGenerator).
 *
 * Resolution order: URL "seed" query parameter -> maze-settings.json "seed" -> Date.now().
 * Resolving once here, instead of once per generator, guarantees both generators
 * see the same seed - which is what makes a maze reproducible from a shared link.
 */

/**
 * Parse a `seed` value out of a query string such as window.location.search.
 * Returns a finite integer, or null when the parameter is absent, empty, or
 * not a number. Never throws - malformed input simply falls through to the
 * caller's fallback instead of breaking page load.
 *
 * @param {string} [search]
 * @returns {number | null}
 */
export function parseSeedFromSearch(search) {
  if (!search) return null;

  const params = new URLSearchParams(search);
  const raw = params.get('seed');

  if (raw === null || raw.trim() === '') return null;

  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return null;

  return Math.trunc(parsed);
}

/**
 * Resolve the single seed for this session.
 *
 * Resolution order:
 *   1. `seed` query parameter - explicit share link, always wins so that a
 *      shared URL reproduces the exact maze it was copied from.
 *   2. `maze-settings.json` "seed" field - explicit null/undefined check so a
 *      configured literal 0 is honoured instead of being treated as falsy.
 *   3. Date.now() - fresh random seed when nothing else is specified.
 *
 * @param {{ seed?: number | null }} [mazeSettings] - parsed maze-settings.json
 *   (only the `seed` field this function reads is typed here - the full
 *   settings shape is out of this contract's scope).
 * @param {string} [search] - a query string, e.g. window.location.search
 * @returns {{ seed: number, source: 'url' | 'settings' | 'random' }}
 */
export function resolveSeed(mazeSettings = {}, search = '') {
  const urlSeed = parseSeedFromSearch(search);
  if (urlSeed !== null) {
    return { seed: urlSeed, source: 'url' };
  }

  if (mazeSettings.seed !== null && mazeSettings.seed !== undefined && Number.isFinite(mazeSettings.seed)) {
    return { seed: mazeSettings.seed, source: 'settings' };
  }

  return { seed: Date.now(), source: 'random' };
}

/**
 * Write or remove the `seed` query parameter in the address bar in place
 * (URLSearchParams + history.replaceState - no navigation, no reload) so a
 * visitor can copy the current maze straight from the URL bar. Preserves any
 * other query parameters already present.
 *
 * @param {boolean} enabled - true to write the seed, false to remove it
 * @param {number} seed - the resolved seed to write when enabled; must be
 *   finite when enabled is true. Callers only ever pass Gallery's already
 *   resolved seed, so a non-finite seed here means the caller is wrong -
 *   this throws instead of silently removing the parameter, which would
 *   look like "sharing disabled" when the caller asked to enable it.
 */
export function setSeedInUrl(enabled, seed) {
  const url = new URL(window.location.href);

  if (enabled) {
    if (!Number.isFinite(seed)) {
      throw new Error(`setSeedInUrl: enabled=true requires a finite seed, got: ${seed}`);
    }
    url.searchParams.set('seed', String(Math.trunc(seed)));
  } else {
    url.searchParams.delete('seed');
  }

  history.replaceState(null, '', url);
}
